import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";

import { putRequestCase, postRequestCase, deleteRequestCase, getRequestCase } from "@/stores/caseStore/backendApi";
import { useCaseStore } from "@/stores/caseStore/0_caseStore";
import { QUESTIONNAIRE_SOURCE } from "@/stores/caseStore/questionnaireSource";
import { useSnackbarStore } from "@/stores/snackbarStore";
import { encryptID } from "@/utils/security";
import * as d from "@/utils/dataUtils";

// model

export const defaultAccident = {
  accidentDate: null,
  accidentTime: "",

  streetType: null,
  streetNo: "",
  zip: "",
  city: "",
  country: null,
  locationType: null,
  driverClientVehicle: null,
  driverId: null,

  accidentDescription: "",
  damageClientVehicle: "",
  damageOpponentVehicle: "",

  clientAdmittedFault: null,
  clientAdmittedFaultTo: null,
  clientAdmittedFaultHow: null,
  clientAdmittedFaultStatement: "",

  opponentAdmittedFault: null,
  opponentAdmittedFaultTo: null,
  opponentAdmittedFaultHow: null,
  opponentAdmittedFaultStatement: "",

  evidenceAvailable: null,
  evidenceTypes: null,

  remarks: "",
};

// CRUD

function encryptedCaseID(): string {
  const store = useCaseStore();
  return encryptID(store.caseData.id);
}

export async function loadAccident(): Promise<void> {
  const store = useCaseStore();
  const response = await getRequestCase(`/protected/cases/${encryptedCaseID()}/accidents`);
  const accident = prepareAccidentForLoad(response);
  store.accident = accident;
  store.lastSavedAccident = cloneDeep(accident);
}

export async function loadAccidentByID(idAccident: number): Promise<void> {
  const store = useCaseStore();
  const response = await getRequestCase(`/protected/cases/${encryptedCaseID()}/accidents/${idAccident}`);
  const accident = prepareAccidentForLoad(response);
  store.accident = accident;
  store.lastSavedAccident = cloneDeep(accident);
}

export async function addAccident(): Promise<void> {
  const store = useCaseStore();
  const response = await postRequestCase(`/protected/cases/${encryptedCaseID()}/accidents`);
  const accident = prepareAccidentForLoad(response);
  store.accident = accident;
  store.lastSavedAccident = cloneDeep(accident);
}

// source: "akte" kennzeichnet die Bearbeitung in der Akte (Bereich „Daten").
// Ist der Unfallhergang freigegeben (Kundenfeedback 22.10.2026), geht eine
// Änderung daran dann in die Akte statt in den Fragebogen und braucht
// confirmReleased – sonst lehnt das Backend sie mit 428 ab.
export async function saveAccident(options: { source?: "akte"; confirmReleased?: boolean } = {}): Promise<void> {
  const store = useCaseStore();
  const snackbar = useSnackbarStore();

  try {
    const current = prepareAccidentForSave(store.accident);
    const previous = prepareAccidentForSave(store.lastSavedAccident);

    if (isEqual(current, previous)) return;

    const params = new URLSearchParams();
    if (options.source === "akte") params.set("source", "akte");
    else if (store.questionnaireView) params.set("source", QUESTIONNAIRE_SOURCE);
    if (options.confirmReleased) params.set("confirmReleased", "1");
    const query = params.toString() ? `?${params.toString()}` : "";
    await putRequestCase(`/protected/cases/${encryptedCaseID()}/accidents/${current.id}${query}`, current);

    store.lastSavedAccident = cloneDeep(store.accident);
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : (error as any)?.message || "Fehler beim Speichern";
    snackbar.showSnackbar({ message: msg, type: "error" });
    throw error;
  }
}

// Kundenfeedback 24.10.2026 (XVI): Korrekturen der Kanzlei an den
// Unfalldaten (Bereich „Daten") gehören nur in die Akte – der Mandant sieht im
// Fragebogen weiter seine eigenen Angaben, keine Vorbelegung durch die Kanzlei.
// Gelesen wird deshalb die Akten-Sicht (?view=cockpit), gespeichert mit
// ?source=akte; das Backend legt die Werte als Akten-Werte ab. Der caseStore
// (Fragebogen-Stand) bleibt unberührt.
export async function loadAccidentAkte(): Promise<Accident> {
  const response = await getRequestCase(`/protected/cases/${encryptedCaseID()}/accidents?view=cockpit`);
  return prepareAccidentForLoad(response);
}

export async function saveAccidentAkte(accident: Accident, options: { confirmReleased?: boolean } = {}): Promise<void> {
  const params = new URLSearchParams({ source: "akte" });
  if (options.confirmReleased) params.set("confirmReleased", "1");
  const { cockpitOverrides: _overrides, descriptionRelease: _release, canReleaseDescription: _canRelease, ...data } = accident;
  await putRequestCase(`/protected/cases/${encryptedCaseID()}/accidents/${accident.id}?${params.toString()}`, prepareAccidentForSave(data));
}

export async function deleteAccident(idAccident: number): Promise<void> {
  const store = useCaseStore();
  await deleteRequestCase(`/protected/cases/${encryptedCaseID()}/accidents/${idAccident}`);
  store.accident = { ...defaultAccident };
  store.lastSavedAccident = { ...defaultAccident };
}

// data helper

type Accident = Record<string, any>;

export function prepareAccidentForLoad(data: Accident): Accident {
  return {
    ...data,

    accidentDate: d.loadDate(data.accidentDate),

    streetType: d.loadSelect(data.streetType),
    country: d.loadSelect(data.country),
    locationType: d.loadSelect(data.locationType),

    driverClientVehicle: d.loadSelect(data.driverClientVehicle),
    driverId: d.loadSelect(data.driverId),

    clientAdmittedFault: d.loadSelect(data.clientAdmittedFault),
    clientAdmittedFaultTo: d.loadArray(data.clientAdmittedFaultTo),
    clientAdmittedFaultHow: d.loadSelect(data.clientAdmittedFaultHow),

    opponentAdmittedFault: d.loadSelect(data.opponentAdmittedFault),
    opponentAdmittedFaultTo: d.loadArray(data.opponentAdmittedFaultTo),
    opponentAdmittedFaultHow: d.loadSelect(data.opponentAdmittedFaultHow),

    evidenceAvailable: d.loadTristate(data.evidenceAvailable),
    evidenceTypes: d.loadArray(data.evidenceTypes),
  };
}

export function prepareAccidentForSave(data: Accident): Accident {
  return {
    ...data,

    accidentDate: d.saveDate(data.accidentDate),

    streetType: d.saveSelect(data.streetType),
    country: d.saveSelect(data.country),
    locationType: d.saveSelect(data.locationType),

    driverClientVehicle: d.saveSelect(data.driverClientVehicle),
    driverId: d.saveSelect(data.driverId),

    clientAdmittedFault: d.saveSelect(data.clientAdmittedFault),
    clientAdmittedFaultTo: d.saveArray(data.clientAdmittedFaultTo),
    clientAdmittedFaultHow: d.saveSelect(data.clientAdmittedFaultHow),

    opponentAdmittedFault: d.saveSelect(data.opponentAdmittedFault),
    opponentAdmittedFaultTo: d.saveArray(data.opponentAdmittedFaultTo),
    opponentAdmittedFaultHow: d.saveSelect(data.opponentAdmittedFaultHow),

    evidenceAvailable: d.saveTristate(data.evidenceAvailable),
    evidenceTypes: d.saveArray(data.evidenceTypes),
  };
}
